/**
 * Recovery for a session that no longer resolves to a user.
 *
 * The client can be holding a token the server can no longer turn into a
 * `skeem_users` row — an expired or revoked session, a token left behind by a
 * previous login, or a user that has since been removed. `fetchMe` throws in
 * that case, and because it is called from effects all over the app the
 * rejection ends up at the error boundary looking exactly like a crash: the 500
 * page, plus an alert emailed to support.
 *
 * None of that is a bug worth reporting. The only useful response is to drop the
 * dead token and reload onto the root, which lands the user on the login screen.
 */

import { errorIs } from "@cd2/error-box"
import { clearBranchGuestSession } from "./branchGuestSession"

/** Where the client keeps the session token, and the key an older build used. */
export const EOS_SKEEM_TOKEN = "EOS-SKEEM-TOKEN"
const LEGACY_SKEEM_TOKEN = "X-SKEEM_AUTH_TOKEN"

const LAST_REDIRECT_KEY = "expiredSessionRedirectAt"
/** Don't bounce round a loop if landing on `/` throws the same error again. */
const MIN_GAP_BETWEEN_REDIRECTS = 15 * 1000

/**
 * Error keys that mean "this client's session is dead", as opposed to any of the
 * other reasons a request can fail. Anything else must keep reporting as normal.
 */
const EXPIRED_SESSION_ERROR_KEYS = [
  // Authenticated, but the user row behind the session has gone
  "skeem.client.fetchMeNotFound",
  // The current user was asked for while the client is anonymous
  "skeem.client.anonFetchMe",
  "skeem.client.clientLoggedOut",
  // The server no longer recognises the token we sent
  "skeem.sessions.authenticatedNoSessionRow",
  "skeem.sessions.noSessionRowFound",
  "skeem.sessions.authenticateMalformedToken",
]

/**
 * `useAsyncEffect` rebuilds a rejection as a plain `Error` before rethrowing it
 * into the boundary, so the ErrorBox that `errorIs` needs is only reachable
 * through `cause`. The original stack is copied across too, and an ErrorBox
 * writes its key into the stack as `[skeem.client.fetchMeNotFound] …`, which is
 * what makes the text check a reliable fallback rather than a guess.
 */
export function isExpiredSessionError(error: unknown): boolean {
  let current: any = error

  for (let depth = 0; current && depth < 5; depth++) {
    if (EXPIRED_SESSION_ERROR_KEYS.some(key => errorIs(current, key))) return true

    const haystack = [
      typeof current.key === "string" ? current.key : "",
      typeof current.message === "string" ? current.message : "",
      typeof current.stack === "string" ? current.stack : "",
    ].join("\n")

    if (EXPIRED_SESSION_ERROR_KEYS.some(key => haystack.includes(key))) return true

    current = current.cause
  }

  return false
}

/** Forget the token locally so the reload starts from a clean, signed out state. */
export function clearStoredSession() {
  try {
    window.localStorage.removeItem(EOS_SKEEM_TOKEN)
    window.localStorage.removeItem(LEGACY_SKEEM_TOKEN)
    // A branch guest session holds the staff member's own token, so it goes too
    clearBranchGuestSession()
  } catch {
    // localStorage throws in private mode — recovery must not depend on it
  }

  try {
    ;(window as any).skeem?.setAuthToken(null)
  } catch {
    // the client may not be initialised yet, which is fine: the reload handles it
  }
}

/**
 * Clear the session and reload onto the root. Returns false when we have already
 * done this moments ago, so the caller can show a message instead of looping.
 */
export function redirectForExpiredSession(): boolean {
  // The token is dead whether or not we navigate, so drop it either way. The
  // fallback UI asks the user to refresh, and that refresh has to start from a
  // signed out state rather than retrying the same dead token.
  clearStoredSession()

  if (redirectRequested) return false

  try {
    const raw = window.sessionStorage.getItem(LAST_REDIRECT_KEY)
    const lastRedirect = raw === null ? NaN : parseInt(raw, 10)

    if (Number.isFinite(lastRedirect) && Date.now() - lastRedirect < MIN_GAP_BETWEEN_REDIRECTS) {
      return false
    }

    window.sessionStorage.setItem(LAST_REDIRECT_KEY, Date.now().toString())
  } catch {
    // Without sessionStorage we can't count attempts across loads, so the latch
    // below is all that holds us to one redirect per page load
  }

  // Only latch once we are committing to the navigation. Setting it earlier
  // meant a refused redirect blocked every later one in the same page load,
  // including a legitimate expiry long after the guard window had passed.
  redirectRequested = true
  window.location.replace("/")

  return true
}

/** Called once the app is up and running, so the next expiry starts fresh. */
export function clearExpiredSessionRedirectState() {
  try {
    window.sessionStorage.removeItem(LAST_REDIRECT_KEY)
  } catch {
    // ignore, see above
  }
}

let redirectRequested = false
